import './shop-gift-card.scss';

import { Gem } from 'lucide-react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import ClockCircleIcon from '@/shared/assets/icons/clock-circle.svg?react';
import CloseCircleIcon from '@/shared/assets/icons/close-circle.svg?react';

import type { ShopGiftListing } from '../../api';

type ShopGiftCardProps = {
  item: ShopGiftListing;
  index: number;
  buying: boolean;
  onRedeem: (item: ShopGiftListing) => void;
  onDetail?: (item: ShopGiftListing) => void;
};

export function ShopGiftCard({ item, index, buying, onRedeem, onDetail }: ShopGiftCardProps) {
  const { t } = useTranslation();

  return (
    <div
      className={`shop-gift-card${item.is_lost ? ' shop-gift-card--lost' : ''}`}
      onClick={() => onDetail?.(item)}
      style={
        {
          cursor: onDetail ? 'pointer' : undefined,
          '--card-in-delay': `${index * 0.04}s`,
        } as CSSProperties
      }>

      <div className="shop-gift-card__media">
        {item.thumbnail_url ? (
          <img
            src={item.thumbnail_url}
            alt={item.name}
            className="shop-gift-card__thumbnail"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
              (e.currentTarget.nextSibling as HTMLElement | null)?.removeAttribute('hidden');
            }}
          />
        ) : null}
        <span
          className="shop-gift-card__emoji"
          hidden={!!item.thumbnail_url || undefined}>
          {item.emoji}
        </span>
      </div>

      <div className="shop-gift-card__body">
        <p className="shop-gift-card__name">{item.name}</p>
        {item.model_name && (
          <p className="shop-gift-card__attr">{item.model_name}</p>
        )}
        {item.is_lost && (
          <span className="shop-gift-card__lost-badge">
            <CloseCircleIcon className="shop-gift-card__lost-icon" />
            {t('shop.filterLost')}
          </span>
        )}
        {item.is_exclusive && (
          <span className="shop-gift-card__exclusive-badge">
            <ClockCircleIcon className="shop-gift-card__lost-icon" />
            {t('shop.exclusive')}
          </span>
        )}
      </div>

      <div className="shop-gift-card__footer">
        <div className="shop-gift-card__price">
          <Gem className="shop-gift-card__price-icon" />
          <span>{item.price_ton.toFixed(2)}</span>
          <span className="shop-gift-card__price-unit">TON</span>
        </div>
        <button
          type="button"
          className="shop-gift-card__btn shop-gift-card__btn--tappable"
          disabled={buying}
          onClick={(e) => { e.stopPropagation(); onRedeem(item); }}>
          {buying ? '...' : t('shop.redeem')}
        </button>
      </div>
      <div className="shop-gift-card__glow" aria-hidden />
    </div>
  );
}
